import { useState, useEffect } from 'react';
import { HeartIcon } from '@heroicons/react/24/outline';
import { HeartIcon as HeartIconSolid } from '@heroicons/react/24/solid';
import { useAuth } from '../hooks/useAuth';
import { subscribeFavoriteStatus, toggleFavorite } from '../lib/favoritesService';

interface FavoriteButtonProps {
  professionalId: string;
  size?: 'sm' | 'md' | 'lg';
  showTooltip?: boolean;
  showLabel?: boolean;
  className?: string;
  onToggle?: (isFavorited: boolean) => void;
}

export default function FavoriteButton({
  professionalId,
  size = 'md',
  showTooltip = true,
  showLabel = false,
  className = '',
  onToggle
}: FavoriteButtonProps) {
  const { user } = useAuth();
  const [isFavorited, setIsFavorited] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);

  // Subscribe to favorite status
  useEffect(() => {
    if (!user?.uid) {
      setIsFavorited(false);
      return;
    }

    const unsubscribe = subscribeFavoriteStatus(
      user.uid,
      professionalId,
      (favorited) => {
        setIsFavorited(favorited);
      }
    );

    return unsubscribe;
  }, [user?.uid, professionalId]);

  const handleToggle = async (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering parent click events

    // Require authentication
    if (!user?.uid) {
      setShowLoginPrompt(true);
      setTimeout(() => setShowLoginPrompt(false), 3000);
      return;
    }

    setIsLoading(true);

    try {
      const newFavoritedState = await toggleFavorite(user.uid, professionalId);

      // Call optional callback
      if (onToggle) {
        onToggle(newFavoritedState);
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Size classes
  const sizeClasses = {
    sm: 'h-5 w-5',
    md: 'h-6 w-6',
    lg: 'h-7 w-7'
  };

  const buttonSizeClasses = {
    sm: 'p-1',
    md: 'p-1.5',
    lg: 'p-2'
  };

  return (
    <div className="relative inline-block">
      <button
        onClick={handleToggle}
        disabled={isLoading}
        className={`
          relative ${showLabel ? 'rounded-[10px] inline-flex items-center justify-center gap-2' : 'rounded-full'}
          ${buttonSizeClasses[size]}
          transition-all duration-200
          ${isFavorited
            ? showLabel ? '' : 'bg-red-50 hover:bg-red-100'
            : showLabel ? '' : 'bg-gray-100 hover:bg-gray-200'
          }
          ${isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
          focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2
          ${className}
        `}
        aria-label={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
        title={showTooltip ? (isFavorited ? 'Remove from favorites' : 'Add to favorites') : undefined}
      >
        {isFavorited ? (
          <HeartIconSolid
            className={`${sizeClasses[size]} text-red-500 animate-in zoom-in-50 duration-200`}
          />
        ) : (
          <HeartIcon
            className={`${sizeClasses[size]} ${showLabel ? 'text-white/70' : 'text-gray-600'}`}
          />
        )}
        {showLabel && (
          <span className="text-[13px] font-medium">
            {isFavorited ? 'Saved' : 'Save'}
          </span>
        )}
      </button>

      {/* Login prompt tooltip */}
      {showLoginPrompt && (
        <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 z-50">
          <div className="bg-gray-900 text-white text-xs rounded-lg py-2 px-3 whitespace-nowrap shadow-lg animate-in fade-in slide-in-from-top-1 duration-200">
            Please sign in to favorite professionals
            <div className="absolute -top-1 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-gray-900 rotate-45"></div>
          </div>
        </div>
      )}
    </div>
  );
}
